
import React, { useState } from 'react';
import { ServiceCategory } from '../../types';
import { Button } from '../ui/Button';
import { Plus, Edit2, Trash2, Save, X, Tag, IndianRupee, CheckCircle, XCircle } from 'lucide-react';
import { saveCategory } from '../../services/firestoreService';

interface AdminCategoriesProps {
    categories: ServiceCategory[];
    onUpdateCategories: (categories: ServiceCategory[]) => void;
}

export const AdminCategories: React.FC<AdminCategoriesProps> = ({ categories, onUpdateCategories }) => {
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ServiceCategory>>({});
    const [isAdding, setIsAdding] = useState(false);
    const [newCategory, setNewCategory] = useState<Partial<ServiceCategory>>({
        name: '',
        basePrice: 100,
        isActive: true,
        parentId: undefined
    });

    const parentCategories = categories.filter(c => !c.parentId);

    const handleEdit = (cat: ServiceCategory) => {
        setIsEditing(cat.id);
        setEditForm(cat);
    };

    const handleSaveEdit = async () => {
        if (!isEditing || !editForm.name) return;
        const previousLabel = document.getElementById('save-btn-text')?.innerText;
        // setSaving(true); // Assuming I add local loading state or reused isAdding for general loading? 
        // Let's use a local saving state for edits handling.

        try {
            const updatedItem = { ...categories.find(c => c.id === isEditing), ...editForm } as ServiceCategory;
            await saveCategory(updatedItem);
            // Don't manually update list. Listener will do it.
            setIsEditing(null);
            setEditForm({});
            // Success Feedback handled by listener update usually, but we can alert briefly
        } catch (e: any) {
            console.error("Failed to save category:", e);
            alert(`Failed to save: ${e.message}`);
        }
    };

    const handleAdd = async () => {
        if (!newCategory.name) return;

        try {
            const uuid = crypto.randomUUID();
            const newItem: ServiceCategory = {
                id: uuid,
                name: newCategory.name,
                basePrice: newCategory.basePrice || 0,
                isActive: newCategory.isActive ?? true,
                parentId: newCategory.parentId
            };

            await saveCategory(newItem);
            // Don't manually update list.
            setIsAdding(false);
            setNewCategory({ name: '', basePrice: 100, isActive: true, parentId: undefined });
        } catch (e: any) {
            console.error("Failed to add category:", e);
            alert(`Failed to add: ${e.message}`);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex justify-between items-center bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                <div>
                    <h2 className="text-xl font-black text-slate-900 leading-tight">Service Management</h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Configure categories and sub-services</p>
                </div>
                <Button onClick={() => setIsAdding(true)} className="rounded-2xl h-12">
                    <Plus className="w-5 h-5 mr-1" /> Add Service
                </Button>
            </div>

            {isAdding && (
                <div className="bg-white border-2 border-blue-100 rounded-[2.5rem] p-8 shadow-xl animate-fadeIn space-y-6">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                            <Plus className="w-6 h-6 text-blue-600" /> New Service Definition
                        </h3>
                        <button onClick={() => setIsAdding(false)} className="text-slate-400 hover:text-red-500 transition-colors"><X className="w-6 h-6" /></button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Parent Category</label>
                            <select
                                value={newCategory.parentId || ''}
                                onChange={(e) => setNewCategory({ ...newCategory, parentId: e.target.value || undefined })}
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-bold outline-none focus:border-blue-500 appearance-none"
                            >
                                <option value="">Main Category (Top Level)</option>
                                {parentCategories.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Service Name</label>
                            <input
                                type="text"
                                value={newCategory.name}
                                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                                placeholder="e.g. Fridge Repair"
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-bold outline-none focus:border-blue-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Base Rate (₹)</label>
                            <input
                                type="number"
                                value={newCategory.basePrice}
                                onChange={(e) => setNewCategory({ ...newCategory, basePrice: Number(e.target.value) })}
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-bold outline-none focus:border-blue-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Display Status</label>
                            <select
                                value={newCategory.isActive ? 'active' : 'inactive'}
                                onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.value === 'active' })}
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-bold outline-none focus:border-blue-500 appearance-none"
                            >
                                <option value="active">Active & Visible</option>
                                <option value="inactive">Hidden (Draft)</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-4 pt-2">
                        <Button variant="ghost" fullWidth onClick={() => setIsAdding(false)} className="h-14 font-black">Discard</Button>
                        <Button fullWidth onClick={handleAdd} className="h-14 rounded-2xl shadow-xl shadow-blue-600/20 font-black">Create Service</Button>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {parentCategories.map(parent => (
                    <div key={parent.id} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
                        {/* Parent Row */}
                        <div className="bg-slate-50/50 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white text-blue-600 rounded-2xl border border-slate-100 shadow-sm">
                                    <Tag className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900">{parent.name}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Primary Category</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-100 rounded-xl">
                                    <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="text-sm font-black text-slate-900">{parent.basePrice}</span>
                                </div>
                                <button onClick={() => handleEdit(parent)} className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-white transition-all rounded-xl border border-transparent hover:border-slate-100">
                                    <Edit2 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Sub-categories */}
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {categories.filter(c => c.parentId === parent.id).map(sub => (
                                <div key={sub.id} className="group relative bg-white hover:bg-blue-50/30 p-4 rounded-2xl border border-slate-100 transition-all">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-black text-slate-800 text-sm group-hover:text-blue-600 transition-colors">{sub.name}</h4>
                                            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase flex items-center gap-1">
                                                <IndianRupee className="w-3 h-3" /> {sub.basePrice} Base
                                            </p>
                                        </div>
                                        <button onClick={() => handleEdit(sub)} className="p-1.5 text-slate-300 group-hover:text-blue-600 transition-colors">
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {!sub.isActive && (
                                        <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded text-[8px] font-black uppercase">
                                            <XCircle className="w-2.5 h-2.5" /> Hidden
                                        </div>
                                    )}
                                </div>
                            ))}
                            <button
                                onClick={() => {
                                    setNewCategory({ ...newCategory, parentId: parent.id });
                                    setIsAdding(true);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="flex items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 text-slate-400 hover:text-blue-600 transition-all font-bold text-xs"
                            >
                                <Plus className="w-4 h-4" /> Add Sub-category
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Edit Modal Overlay */}
            {isEditing && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-md p-10 shadow-2xl animate-scaleIn space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-2xl font-black text-slate-900">Update Service</h3>
                            <button onClick={() => setIsEditing(null)} className="text-slate-300 hover:text-red-500 transition-colors"><X className="w-6 h-6" /></button>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Service Name</label>
                                <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-bold outline-none focus:border-blue-500"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Base Price (₹)</label>
                                <input
                                    type="number"
                                    value={editForm.basePrice}
                                    onChange={(e) => setEditForm({ ...editForm, basePrice: Number(e.target.value) })}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-bold outline-none focus:border-blue-500"
                                />
                            </div>
                            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                <input
                                    type="checkbox"
                                    id="is_active_edit"
                                    checked={editForm.isActive}
                                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                                    className="w-5 h-5 rounded-lg border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor="is_active_edit" className="text-xs font-black text-slate-600 uppercase tracking-widest cursor-pointer">Service is Active</label>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <Button variant="ghost" fullWidth onClick={() => setIsEditing(null)} className="h-14 font-black">Cancel</Button>
                            <Button fullWidth onClick={handleSaveEdit} className="h-14 rounded-2xl shadow-xl shadow-blue-600/20 font-black">Save Changes</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
